import React, {useRef} from "react";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import classNames from "classnames";
import Paper from "@material-ui/core/Paper";
import {observer} from "mobx-react-lite";
import {StateService} from "../services/StateService";
import CustomTable from "../components/CustomTable";
import MouseArea, {MouseAreaStage} from "../components/MouseArea";
import {Toast, ToastProps} from "../components/Toast";
import {BasicHeroSection} from "../components/BasicHeroSection";
import {InfoBox} from "../components/InfoBox";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      textAlign: "center",
      alignItems: "center",
      minHeight: "100vh",
      paddingTop: "65px",
      maxWidth: "1400px",
      margin: "0 auto",
    },
    countDown: {
      width: "100%",
      color: "white",
      borderRadius: "20px",
      position: "relative",
    },
    countDownTime: {
      width: "98%",
      margin: "0 auto",
      marginTop: "20px",
      paddingBottom: "15px",
    },
    paper: {
      color: theme.palette.text.secondary,
      backgroundColor: "transparent",
      padding: "20px",
      borderRadius: "19px",
    },
    heading: {
      display: "flex",
      color: "#F782ED",
      fontWeight: 500,
      fontSize: 36,
      fontFamily: 'Gilroy-Medium, sans-serif',
      alignItems: 'center',
    },
    status: {
      padding: "5px",
      fontSize: "12px",
      borderRadius: "10px",
      background: "#E06963",
      color: "black",
    },
    stausSubmitted: {
      background: "#7AB169",
    },
    button: {
      color: "white !important",
      backgroundColor: "#3f51b5 !important",
      marginTop: 10,
    },
    container: {
      marginTop: 100,
      position: "relative",
    },
    leftLayout: {
      position: 'absolute',
      width: '592px',
      height: '460px',
      left: '-32px',
      bottom: '8px',
      background: 'linear-gradient(199.55deg, rgba(72, 46, 95, 0.225033) 40.83%, #503045 105.46%)',
      mixBlendMode: 'normal',
      opacity: '0.9',
      filter: 'blur(135.914px)',
    },
    rightLayout: {
      position: 'absolute',
      width: '433px',
      height: '461px',
      right: '13px',
      top: '0px',
      background: 'linear-gradient(190.81deg, #503045 40.52%, rgba(72, 46, 95, 0.225033) 86.44%)',
      mixBlendMode: 'normal',
      opacity: 0.9,
      filter: 'blur(135.914px)',
      transform: 'rotate(90deg)',
    },
    bottomLayout: {
      position: 'absolute',
      width: '331px',
      height: '209px',
      right: '-156px',
      bottom: 0,
      background: 'linear-gradient(190.81deg, #503045 40.52%, rgba(72, 46, 95, 0.225033) 86.44%)',
      mixBlendMode: 'normal',
      opacity: 0.9,
      filter: 'blur(135.914px)',
    },
    innerLay: {
      background: '#6E37FF',
      mixBlendMode: 'normal',
      opacity: 0.12,
      filter: 'blur(135.914px)',
      width: '771px',
      height: '520px',
    },
    innerLay1: {
      background: '#3772FF',
      mixBlendMode: 'normal',
      opacity: 0.12,
      filter: 'blur(135.914px)',
      width: '771px',
      height: '520px',
    },
    rightBottomLayout: {
      position: "absolute",
      width: "429px",
      height: "426px",
      right: "-12px",
      top: "325px",
      background: "linear-gradient(190.81deg, #503045 40.52%, rgba(72, 46, 95, 0.225033) 86.44%)",
      mixBlendMode: "normal",
      opacity: 0.9,
      filter: "blur(135.914px)",
    },
    BottominnerLay: {
      width: "522px",
      height: "349px",
      background: "#6E37FF",
      mixBlendMode: "normal",
      opacity: 0.12,
      filter: "blur(135.914px)",
    },
    BottominnerLay1: {
      width: "515px",
      height: "295px",
      background: "#3772FF",
      mixBlendMode: "normal",
      opacity: 0.12,
      filter: "blur(135.914px)",
    },
  })
);

interface BuildPageProps {
  state: StateService;
}

const headers = [
  { key: "address", label: "Address" },
  { key: "entropyStatus", label: "Entropy Status" },
];

const BuildPage = observer((props: BuildPageProps) => {
  const classes = useStyles();
  const messageToast = useRef<ToastProps>(null);
  const current_time = Date.now();
  const end_time = (props.state.rounds.getPhaseEndTime("entropy") ?? new Date()).getTime();

  let stage = MouseAreaStage.DISABLED;
  if (props.state.authentication.getAuthStatus()) {
    if (
      props.state.entropy.hasSubmittedEntropy(
        props.state.authentication.getAddress()
      )
    ) {
      stage = MouseAreaStage.SUBMITTED;
    } else {
      stage = MouseAreaStage.ENABLED;
    }
  }

  const submitEntropy = async (squigglerEntropy: Number) => {

    let address = props.state.authentication.getAddress();
    if (address === "NONE" || !props.state.web3.isAvailable()) {
      messageToast?.current?.showMessage("Login first!", "info");
      return;
    }

    let response = await props.state.api.submitEntropy(
      squigglerEntropy.toString()
    );
    if (response.statusCode === 200) {
      props.state.syncEntropy();
      messageToast?.current?.showMessage("Entropy submitted!", "info");
    } else {
      messageToast?.current?.showMessage("Failed to submit entropy!", "error");
      console.log(response.detail);
    }
  };

  const getEntropyStatus = (submitted: boolean) => {
    if (submitted) {
      return (
        <span className={classNames(classes.status, classes.stausSubmitted)}>
          Generated
        </span>
      );
    }
    return <span className={classes.status}>Not Generated</span>;
  };

  return (
    <div className={classes.root}>
      <Toast ref={messageToast} />
      <div className={classes.countDown}>
      <div className={classes.leftLayout}></div>
        <div className={classes.rightLayout}>
          <div className={classes.innerLay}></div>
          <div className={classes.innerLay1}></div>
        </div>
        <div className={classes.countDownTime}>
          <BasicHeroSection
            title="BUILD CLOSES IN"
            state={props.state}
            countDownDate={
              props.state.rounds.isBuildPhase()
                ? props.state.rounds.getPhaseEndTime("entropy")
                : undefined
            }
          />
        </div>
      </div>
      <Grid container spacing={0} className={classes.container}>
        <Grid item xs={6}>
          <Paper className={classes.paper} elevation={0}>
            <div className={classes.heading}>Entropy Status
              <InfoBox
                  title="This table shows who has submitted entropy."
              />
            </div>
            <CustomTable
              headers={headers}
              data={props.state.entropy.getEntropy().map((v, i) => ({
                address: v.address,
                entropyStatus: getEntropyStatus(v.has_submitted),
              }))}
              currentUser={props.state.authentication.getAddress() || ""}
              noDataTitle="No entropy has yet been generated."
            />
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper className={classes.paper} elevation={0}>
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <MouseArea
                  stage={stage}
                  submitEntropy={submitEntropy}
                  disabled={!props.state.rounds.isBuildPhase() || current_time > end_time}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <div className={classes.rightBottomLayout}>
            <div className={classes.BottominnerLay}>
            </div>
            <div className={classes.BottominnerLay1}>
            </div>
          </div>
      </Grid>
    </div>
  );
});

export default BuildPage;
